import Fuse from 'fuse.js'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Edit2,
  ExternalLink,
  Layers,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { activatableRowProps } from '@/components/admin/shared/activatable-row'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { StudyPlanFormModal } from '@/components/admin/shared/study-plan-form-modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  REVEAL_ON_HOVER,
  REVEAL_ON_HOVER_DECORATIVE,
} from '@/components/ui/reveal-on-hover'
import { getDifficultyColorClass, getDifficultyLabel } from '@/lib/difficulty'
import { cn } from '@/lib/utils'
import { useResources } from '../system-design/hooks/use-resources'
import { useSystemDesignProblems } from '../system-design/hooks/use-system-design-problems'
import { RESOURCE_TYPE_COLORS } from './constants'
import type {
  StudyPlan,
  StudyPlanFormData,
  StudyPlanResourceItem,
  SystemDesignStudyPlanProblem,
} from './hooks/use-study-plans'
import { useStudyPlans } from './hooks/use-study-plans'

// ---------------------------------------------------------------------------
// Study Plan Detail Modal (shows SD problems, search/add/remove)
// ---------------------------------------------------------------------------

interface SDStudyPlanDetailModalProps {
  studyPlan: StudyPlan
  onClose: () => void
  onAddProblems: (problemIds: string[]) => Promise<void>
  onRemoveProblem: (problemId: string) => Promise<void>
  onAddResources: (resourceIds: string[]) => Promise<void>
  onRemoveResource: (resourceId: string) => Promise<void>
}

function SDStudyPlanDetailModal({
  studyPlan,
  onClose,
  onAddProblems,
  onRemoveProblem,
  onAddResources,
  onRemoveResource,
}: SDStudyPlanDetailModalProps) {
  const { problems: allSDProblems, loading: sdLoading } =
    useSystemDesignProblems()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [isAdding, setIsAdding] = useState(false)
  const [showAddSection, setShowAddSection] = useState(false)

  const sdProblems: SystemDesignStudyPlanProblem[] =
    studyPlan.systemDesignProblems || []

  const existingIds = useMemo(
    () => new Set(sdProblems.map((p) => p.id)),
    [sdProblems],
  )

  const filteredProblems = useMemo(() => {
    if (!searchQuery.trim()) return []
    const query = searchQuery.toLowerCase()
    return allSDProblems
      .filter(
        (p) =>
          !existingIds.has(p.id) &&
          (p.title.toLowerCase().includes(query) ||
            p.companyTags.some((t) => t.name.toLowerCase().includes(query)) ||
            p.topics.some((t) => t.toLowerCase().includes(query))),
      )
      .slice(0, 20)
  }, [allSDProblems, searchQuery, existingIds])

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleAddSelected = async () => {
    if (selectedIds.size === 0) return
    setIsAdding(true)
    try {
      await onAddProblems(Array.from(selectedIds))
      setSelectedIds(new Set())
      setSearchQuery('')
      setShowAddSection(false)
      toast.success(`Added ${selectedIds.size} problem(s)`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add problems')
    } finally {
      setIsAdding(false)
    }
  }

  const handleRemove = async (problemId: string) => {
    try {
      await onRemoveProblem(problemId)
      toast.success('Problem removed')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to remove problem',
      )
    }
  }

  // --- Resources state ---
  const {
    resources: allResources,
    loading: resourcesLoading,
    createResource,
  } = useResources()
  const [resourceSearchQuery, setResourceSearchQuery] = useState('')
  const [selectedResourceIds, setSelectedResourceIds] = useState<Set<string>>(
    () => new Set(),
  )
  const [isAddingResources, setIsAddingResources] = useState(false)
  const [showAddResourcesSection, setShowAddResourcesSection] = useState(false)
  const [showNewResourceForm, setShowNewResourceForm] = useState(false)
  const [newResourceData, setNewResourceData] = useState({
    title: '',
    url: '',
    type: 'article',
  })
  const [isCreatingResource, setIsCreatingResource] = useState(false)

  const planResources: StudyPlanResourceItem[] = studyPlan.resources || []

  const existingResourceIds = useMemo(
    () => new Set(planResources.map((r) => r.id)),
    [planResources],
  )

  const resourceFuse = useMemo(
    () =>
      new Fuse(
        allResources.filter((r) => !existingResourceIds.has(r.id)),
        {
          keys: ['title', 'url'],
          threshold: 0.4,
        },
      ),
    [allResources, existingResourceIds],
  )

  const filteredResources = useMemo(() => {
    if (!resourceSearchQuery.trim()) return []
    return resourceFuse
      .search(resourceSearchQuery)
      .slice(0, 20)
      .map((r) => r.item)
  }, [resourceFuse, resourceSearchQuery])

  const handleResourceToggle = (id: string) => {
    setSelectedResourceIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleAddSelectedResources = async () => {
    if (selectedResourceIds.size === 0) return
    setIsAddingResources(true)
    try {
      await onAddResources(Array.from(selectedResourceIds))
      setSelectedResourceIds(new Set())
      setResourceSearchQuery('')
      setShowAddResourcesSection(false)
      toast.success(`Added ${selectedResourceIds.size} resource(s)`)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to add resources',
      )
    } finally {
      setIsAddingResources(false)
    }
  }

  const handleRemoveResource = async (resourceId: string) => {
    try {
      await onRemoveResource(resourceId)
      toast.success('Resource removed')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to remove resource',
      )
    }
  }

  const handleCreateAndLinkResource = async () => {
    if (!newResourceData.title.trim() || !newResourceData.url.trim()) {
      toast.error('Title and URL are required')
      return
    }
    setIsCreatingResource(true)
    try {
      const created = await createResource({
        title: newResourceData.title,
        url: newResourceData.url,
        type: newResourceData.type,
      })
      await onAddResources([created.id])
      setNewResourceData({ title: '', url: '', type: 'article' })
      setShowNewResourceForm(false)
      toast.success('Resource created and linked')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to create resource',
      )
    } finally {
      setIsCreatingResource(false)
    }
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className="bg-card border-border relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b p-6">
          <div className="flex items-center gap-4">
            <AuthoredIcon
              icon={studyPlan.icon}
              size="2xl"
              className="text-muted-foreground rounded"
              fallback={Layers}
              fallbackChip
            />
            <div>
              <h2 className="text-foreground text-xl font-semibold">
                {studyPlan.name}
              </h2>
              {studyPlan.description && (
                <p className="text-muted-foreground text-sm">
                  {studyPlan.description}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Problems Section */}
          <div className="mb-6">
            {!showAddSection ? (
              <Button
                variant="outline"
                onClick={() => setShowAddSection(true)}
                className="w-full"
              >
                <Plus size={18} className="mr-2" />
                Add System Design Problems
              </Button>
            ) : (
              <div className="border-border rounded-lg border p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-foreground font-medium">
                    Add System Design Problems
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSection(false)
                      setSearchQuery('')
                      setSelectedIds(new Set())
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="relative mb-3">
                  <Search
                    size={16}
                    className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                  />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by title, company tag, or topic..."
                    className="pl-9"
                  />
                </div>
                {sdLoading ? (
                  <div className="text-muted-foreground flex items-center gap-2 py-4 text-center text-sm">
                    <RefreshCw size={14} className="animate-spin" />
                    Loading problems...
                  </div>
                ) : searchQuery.trim() ? (
                  filteredProblems.length > 0 ? (
                    <div className="max-h-48 space-y-1 overflow-y-auto">
                      {filteredProblems.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => handleToggle(p.id)}
                          className={`hover:bg-muted/50 flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors ${
                            selectedIds.has(p.id) ? 'bg-primary/10' : ''
                          }`}
                        >
                          <div
                            className={`flex h-5 w-5 items-center justify-center rounded border ${
                              selectedIds.has(p.id)
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-muted-foreground'
                            }`}
                          >
                            {selectedIds.has(p.id) && <Check size={12} />}
                          </div>
                          <div className="flex-1">
                            <span className="text-foreground text-sm">
                              {p.title}
                            </span>
                            <div className="flex items-center gap-2">
                              {p.companyTags.length > 0 && (
                                <span className="text-muted-foreground text-xs">
                                  {p.companyTags.map((t) => t.name).join(', ')}
                                </span>
                              )}
                              {p.topics.length > 0 && (
                                <span className="text-muted-foreground/70 text-xs">
                                  {p.topics.slice(0, 3).join(', ')}
                                </span>
                              )}
                            </div>
                          </div>
                          {p.difficulty && (
                            <span
                              className={`rounded px-1.5 py-0.5 text-xs font-medium ${getDifficultyColorClass(p.difficulty)}`}
                            >
                              {getDifficultyLabel(p.difficulty)}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      No matching problems found
                    </p>
                  )
                ) : (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    Start typing to search problems...
                  </p>
                )}
                {selectedIds.size > 0 && (
                  <div className="mt-3 flex justify-end">
                    <Button
                      onClick={handleAddSelected}
                      disabled={isAdding}
                      size="sm"
                    >
                      {isAdding
                        ? 'Adding...'
                        : `Add ${selectedIds.size} Problem(s)`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Existing Problems */}
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-foreground text-lg font-medium">
              Problems ({sdProblems.length})
            </h3>
          </div>

          {sdProblems.length === 0 ? (
            <div className="py-12 text-center">
              <div className="bg-muted/50 mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full">
                <Layers size={32} className="text-muted-foreground" />
              </div>
              <h4 className="text-foreground mb-2 font-medium">
                No problems yet
              </h4>
              <p className="text-muted-foreground mx-auto max-w-sm text-sm">
                Add system design problems to this study plan using the button
                above.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sdProblems.map((problem) => (
                <div
                  key={problem.id}
                  className="bg-muted/30 hover:bg-muted/50 group flex items-center justify-between rounded-lg p-4 transition-colors"
                >
                  <div className="flex-1">
                    <h4 className="text-foreground font-medium">
                      {problem.title}
                    </h4>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {problem.difficulty && (
                        <span
                          className={`rounded px-2 py-0.5 text-xs font-medium ${getDifficultyColorClass(problem.difficulty)}`}
                        >
                          {getDifficultyLabel(problem.difficulty)}
                        </span>
                      )}
                      {problem.companyTags.length > 0 && (
                        <span className="text-muted-foreground text-xs">
                          {problem.companyTags.join(', ')}
                        </span>
                      )}
                      {problem.topics.length > 0 && (
                        <span className="text-muted-foreground/70 text-xs">
                          {problem.topics.join(', ')}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(problem.id)}
                    className={cn(
                      REVEAL_ON_HOVER,
                      'text-muted-foreground hover:text-destructive ml-2 rounded p-2 transition-all',
                    )}
                    title="Remove from study plan"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* ---- Resources Section ---- */}
          <div className="border-border mt-8 border-t pt-6">
            {/* Add Resources */}
            <div className="mb-6">
              {!showAddResourcesSection ? (
                <Button
                  variant="outline"
                  onClick={() => setShowAddResourcesSection(true)}
                  className="w-full"
                >
                  <Plus size={18} className="mr-2" />
                  Add Resources
                </Button>
              ) : (
                <div className="border-border rounded-lg border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-foreground font-medium">
                      Add Resources
                    </h3>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddResourcesSection(false)
                        setResourceSearchQuery('')
                        setSelectedResourceIds(new Set())
                        setShowNewResourceForm(false)
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="relative mb-3">
                    <Search
                      size={16}
                      className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2"
                    />
                    <Input
                      type="text"
                      value={resourceSearchQuery}
                      onChange={(e) => setResourceSearchQuery(e.target.value)}
                      placeholder="Search resources by title or URL..."
                      className="pl-9"
                    />
                  </div>
                  {resourcesLoading ? (
                    <div className="text-muted-foreground flex items-center gap-2 py-4 text-center text-sm">
                      <RefreshCw size={14} className="animate-spin" />
                      Loading resources...
                    </div>
                  ) : resourceSearchQuery.trim() ? (
                    filteredResources.length > 0 ? (
                      <div className="max-h-48 space-y-1 overflow-y-auto">
                        {filteredResources.map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => handleResourceToggle(r.id)}
                            className={`hover:bg-muted/50 flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors ${
                              selectedResourceIds.has(r.id)
                                ? 'bg-primary/10'
                                : ''
                            }`}
                          >
                            <div
                              className={`flex h-5 w-5 items-center justify-center rounded border ${
                                selectedResourceIds.has(r.id)
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-muted-foreground'
                              }`}
                            >
                              {selectedResourceIds.has(r.id) && (
                                <Check size={12} />
                              )}
                            </div>
                            <div className="flex-1">
                              <span className="text-foreground text-sm">
                                {r.title}
                              </span>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`rounded px-1.5 py-0.5 text-xs font-medium ${RESOURCE_TYPE_COLORS[r.type] || 'bg-gray-500/10 text-gray-600'}`}
                                >
                                  {r.type}
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground py-4 text-center text-sm">
                        No matching resources found
                      </p>
                    )
                  ) : (
                    <p className="text-muted-foreground py-4 text-center text-sm">
                      Start typing to search resources...
                    </p>
                  )}
                  {selectedResourceIds.size > 0 && (
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={handleAddSelectedResources}
                        disabled={isAddingResources}
                        size="sm"
                      >
                        {isAddingResources
                          ? 'Adding...'
                          : `Add ${selectedResourceIds.size} Resource(s)`}
                      </Button>
                    </div>
                  )}

                  {/* Create New Resource Inline */}
                  <div className="border-border mt-3 border-t pt-3">
                    {!showNewResourceForm ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowNewResourceForm(true)}
                        className="w-full"
                      >
                        <Plus size={16} className="mr-1" />
                        Add New Resource
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <Input
                          type="text"
                          value={newResourceData.title}
                          onChange={(e) =>
                            setNewResourceData({
                              ...newResourceData,
                              title: e.target.value,
                            })
                          }
                          placeholder="Resource title"
                        />
                        <Input
                          type="url"
                          value={newResourceData.url}
                          onChange={(e) =>
                            setNewResourceData({
                              ...newResourceData,
                              url: e.target.value,
                            })
                          }
                          placeholder="https://..."
                        />
                        <select
                          value={newResourceData.type}
                          onChange={(e) =>
                            setNewResourceData({
                              ...newResourceData,
                              type: e.target.value,
                            })
                          }
                          className="border-input bg-background w-full rounded-md border p-2 text-sm"
                        >
                          <option value="video">Video</option>
                          <option value="article">Article</option>
                          <option value="course">Course</option>
                          <option value="book">Book</option>
                          <option value="tool">Tool</option>
                        </select>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setShowNewResourceForm(false)
                              setNewResourceData({
                                title: '',
                                url: '',
                                type: 'article',
                              })
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleCreateAndLinkResource}
                            disabled={isCreatingResource}
                          >
                            {isCreatingResource
                              ? 'Creating...'
                              : 'Create & Link'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Existing Resources */}
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-foreground text-lg font-medium">
                Resources ({planResources.length})
              </h3>
            </div>

            {planResources.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-muted-foreground text-sm">
                  No resources yet
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {planResources.map((resource) => (
                  <div
                    key={resource.id}
                    className="bg-muted/30 hover:bg-muted/50 group flex items-center justify-between rounded-lg p-4 transition-colors"
                  >
                    <div className="flex flex-1 items-center gap-3">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${RESOURCE_TYPE_COLORS[resource.type] || 'bg-gray-500/10 text-gray-600'}`}
                      >
                        {resource.type}
                      </span>
                      <h4 className="text-foreground flex-1 font-medium">
                        {resource.title}
                      </h4>
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-foreground rounded p-1 transition-colors"
                        title={resource.url}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={14} />
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveResource(resource.id)}
                      className={cn(
                        REVEAL_ON_HOVER,
                        'text-muted-foreground hover:text-destructive ml-2 rounded p-2 transition-all',
                      )}
                      title="Remove from study plan"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Section
// ---------------------------------------------------------------------------

export function SystemDesignStudyPlansSection() {
  const {
    studyPlans,
    loading,
    error,
    refresh,
    createStudyPlan,
    updateStudyPlan,
    deleteStudyPlan,
    addSystemDesignProblemsToStudyPlan,
    removeSystemDesignProblemsFromStudyPlan,
    addResourcesToStudyPlan,
    removeResourcesFromStudyPlan,
  } = useStudyPlans(undefined, 'system_design')

  const [isCollapsed, setIsCollapsed] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editingPlan, setEditingPlan] = useState<StudyPlan | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<StudyPlan | null>(null)
  const [planToDelete, setPlanToDelete] = useState<StudyPlan | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleCreateOrUpdate = async (data: StudyPlanFormData) => {
    setIsSubmitting(true)
    try {
      if (editingPlan) {
        await updateStudyPlan(editingPlan.id, data)
        toast.success('Study plan updated')
      } else {
        await createStudyPlan(data)
        toast.success('Study plan created')
      }
      setShowModal(false)
      setEditingPlan(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEdit = (e: React.MouseEvent, plan: StudyPlan) => {
    e.stopPropagation()
    setEditingPlan(plan)
    setShowModal(true)
  }

  const handleDelete = (e: React.MouseEvent, plan: StudyPlan) => {
    e.stopPropagation()
    setPlanToDelete(plan)
    setShowDeleteConfirm(true)
  }

  const handleConfirmDelete = async () => {
    if (!planToDelete) return
    setIsSubmitting(true)
    try {
      await deleteStudyPlan(planToDelete.id)
      toast.success('Study plan deleted')
      setShowDeleteConfirm(false)
      setPlanToDelete(null)
      if (selectedPlan?.id === planToDelete.id) {
        setShowDetailModal(false)
        setSelectedPlan(null)
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePlanClick = (plan: StudyPlan) => {
    setSelectedPlan(plan)
    setShowDetailModal(true)
  }

  const handleAddProblems = async (problemIds: string[]) => {
    if (!selectedPlan) return
    await addSystemDesignProblemsToStudyPlan(selectedPlan.id, problemIds)
    // Note: selectedPlan updates automatically via currentSelectedPlan derived value
  }

  const handleRemoveProblem = async (problemId: string) => {
    if (!selectedPlan) return
    await removeSystemDesignProblemsFromStudyPlan(selectedPlan.id, [problemId])
    // Note: selectedPlan updates automatically via currentSelectedPlan derived value
  }

  const handleAddResources = async (resourceIds: string[]) => {
    if (!selectedPlan) return
    await addResourcesToStudyPlan(selectedPlan.id, resourceIds)
    // Note: selectedPlan updates automatically via currentSelectedPlan derived value
  }

  const handleRemoveResource = async (resourceId: string) => {
    if (!selectedPlan) return
    await removeResourcesFromStudyPlan(selectedPlan.id, [resourceId])
    // Note: selectedPlan updates automatically via currentSelectedPlan derived value
  }

  // Keep selected plan in sync with studyPlans
  const currentSelectedPlan = selectedPlan
    ? studyPlans.find((sp) => sp.id === selectedPlan.id) || selectedPlan
    : null

  if (loading) {
    return (
      <div className="bg-card border-border mt-8 rounded-lg border">
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hover:bg-muted/50 flex w-full items-center justify-between p-4 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 p-2">
              <Layers size={20} className="text-white" />
            </div>
            <div className="text-left">
              <h2 className="text-foreground text-lg font-semibold">
                System Design Study Plans
              </h2>
              <p className="text-muted-foreground text-sm">Loading...</p>
            </div>
          </div>
          {isCollapsed ? (
            <ChevronDown size={20} className="text-muted-foreground" />
          ) : (
            <ChevronUp size={20} className="text-muted-foreground" />
          )}
        </button>
        {!isCollapsed && (
          <div className="border-border border-t p-4">
            <div className="text-muted-foreground flex items-center gap-2">
              <RefreshCw size={16} className="animate-spin" />
              Loading study plans...
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="bg-card border-border mt-8 rounded-lg border">
      {/* Collapsible Header */}
      <div className="flex items-center justify-between p-4">
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="hover:bg-muted/50 -m-2 flex flex-1 items-center gap-3 rounded-lg p-2 transition-colors"
        >
          <div className="rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 p-2">
            <Layers size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h2 className="text-foreground text-lg font-semibold">
              System Design Study Plans
            </h2>
            <p className="text-muted-foreground text-sm">
              {studyPlans.length} study plan
              {studyPlans.length !== 1 ? 's' : ''}
            </p>
          </div>
          {isCollapsed ? (
            <ChevronDown size={20} className="text-muted-foreground" />
          ) : (
            <ChevronUp size={20} className="text-muted-foreground" />
          )}
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowModal(true)}>
            <Plus size={16} className="mr-1" />
            Add Study Plan
          </Button>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="border-border border-t p-4">
          {error ? (
            <div className="py-8 text-center">
              <p className="text-muted-foreground mb-2">{error}</p>
              <Button variant="outline" size="sm" onClick={refresh}>
                Retry
              </Button>
            </div>
          ) : studyPlans.length === 0 ? (
            <div className="py-8 text-center">
              <div className="bg-muted mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full">
                <Layers size={24} className="text-muted-foreground" />
              </div>
              <h3 className="text-foreground mb-2 font-medium">
                No system design study plans yet
              </h3>
              <p className="text-muted-foreground mb-4 text-sm">
                Create your first system design study plan to organize problems.
              </p>
              <Button size="sm" onClick={() => setShowModal(true)}>
                <Plus size={16} className="mr-1" />
                Create Study Plan
              </Button>
            </div>
          ) : (
            <div className="space-y-1">
              {studyPlans.map((plan) => (
                <div
                  key={plan.id}
                  {...activatableRowProps({
                    onActivate: () => handlePlanClick(plan),
                    label: `Open ${plan.name}`,
                  })}
                  className="hover:bg-muted/50 group flex cursor-pointer items-center gap-4 rounded-lg p-3 transition-colors"
                >
                  <AuthoredIcon
                    icon={plan.icon}
                    size="xl"
                    className="text-muted-foreground rounded"
                    fallback={Layers}
                    fallbackChip
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-foreground font-medium">
                        {plan.name}
                      </h3>
                      <span className="bg-primary/10 text-primary rounded px-2 py-0.5 text-xs font-medium">
                        {plan.problemCount}{' '}
                        {plan.problemCount === 1 ? 'problem' : 'problems'}
                      </span>
                    </div>
                    {plan.description && (
                      <p className="text-muted-foreground line-clamp-1 text-sm">
                        {plan.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => handleEdit(e, plan)}
                      className={cn(
                        REVEAL_ON_HOVER,
                        'text-muted-foreground hover:text-foreground rounded p-2 transition-all',
                      )}
                      title="Edit study plan"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, plan)}
                      className={cn(
                        REVEAL_ON_HOVER,
                        'text-muted-foreground hover:text-destructive rounded p-2 transition-all',
                      )}
                      title="Delete study plan"
                    >
                      <Trash2 size={16} />
                    </button>
                    <ChevronRight
                      size={18}
                      className={cn(
                        REVEAL_ON_HOVER_DECORATIVE,
                        'text-muted-foreground',
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <StudyPlanFormModal
        isOpen={showModal}
        studyPlan={editingPlan}
        onClose={() => {
          setShowModal(false)
          setEditingPlan(null)
        }}
        onSubmit={handleCreateOrUpdate}
        isSubmitting={isSubmitting}
        namePlaceholder="e.g., Top SD Questions"
        descriptionPlaceholder="A curated list of system design problems..."
      />

      {/* Detail Modal */}
      {showDetailModal && currentSelectedPlan && (
        <SDStudyPlanDetailModal
          studyPlan={currentSelectedPlan}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedPlan(null)
          }}
          onAddProblems={handleAddProblems}
          onRemoveProblem={handleRemoveProblem}
          onAddResources={handleAddResources}
          onRemoveResource={handleRemoveResource}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && planToDelete && (
        <div className="bg-background/80 fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border-border w-full max-w-md rounded-lg border p-6 shadow-lg">
            <div className="mb-4 flex items-center gap-3">
              <div className="bg-destructive/10 flex h-10 w-10 items-center justify-center rounded-full">
                <AlertTriangle size={20} className="text-destructive" />
              </div>
              <div>
                <h2 className="text-foreground text-lg font-semibold">
                  Delete Study Plan
                </h2>
                <p className="text-muted-foreground text-sm">
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <p className="text-foreground mb-6">
              Are you sure you want to delete{' '}
              <strong>{planToDelete.name}</strong>? This will remove the study
              plan but won't affect the problems themselves.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setPlanToDelete(null)
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
